import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";

const IMAGES = [
  "/images/seq-1-terrain.webp",
  "/images/seq-2-croquis.webp",
  "/images/seq-3-3d.webp",
  "/images/seq-4-construction.webp",
  "/images/seq-5-villa.webp",
];

const FADE = 0.05;

const Stage = ({ index, total, stage, img, progress }) => {
  const seg = 1 / total;
  const start = index * seg;
  const end = start + seg;
  const first = index === 0;
  const last = index === total - 1;

  const opacity = useTransform(
    progress,
    first ? [end - FADE, end + FADE] : last ? [start - FADE, start + FADE] : [start - FADE, start + FADE, end - FADE, end + FADE],
    first ? [1, 0] : last ? [0, 1] : [0, 1, 1, 0]
  );
  const scale = useTransform(progress, [start - FADE, end + FADE], [1.06, 1]);
  const textY = useTransform(progress, [start - FADE, start + FADE], [30, 0]);

  return (
    <motion.div style={{ opacity }} className="absolute inset-0" data-testid={`transform-stage-${index + 1}`}>
      <motion.img src={img} alt={stage.label} loading="lazy" decoding="async" style={{ scale }} className="h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#111827]/85 via-transparent to-[#111827]/40" />
      <motion.div style={{ y: textY }} className="absolute bottom-24 left-0 right-0 px-6 md:bottom-28 md:px-14">
        <div className="mx-auto max-w-7xl">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#4DB8FF]">
            {String(index + 1).padStart(2, "0")} — {stage.label}
          </p>
          <h3 className="mt-3 max-w-2xl font-display text-3xl text-white md:text-5xl">{stage.title}</h3>
          <p className="mt-3 max-w-md text-sm font-light leading-relaxed text-white/75 md:text-base">{stage.desc}</p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Transformation = () => {
  const { t } = useTranslation();
  const stages = t("transform.stages", { returnObjects: true });
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setActive(Math.min(stages.length - 1, Math.max(0, Math.floor(v * stages.length))));
  });

  return (
    <section ref={ref} className="relative" style={{ height: "500vh" }} data-testid="transformation-section">
      <div className="grain sticky top-0 h-screen overflow-hidden bg-[#111827]">
        <div className="absolute left-0 right-0 top-24 z-20 px-6 text-center md:top-28">
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-[#4DB8FF]">{t("transform.kicker")}</p>
          <h2 className="mt-2 font-display text-xl text-white/90 md:text-2xl">{t("transform.title")}</h2>
        </div>

        {stages.map((s, i) => (
          <Stage key={i} index={i} total={stages.length} stage={s} img={IMAGES[i]} progress={scrollYProgress} />
        ))}

        <div className="absolute bottom-8 left-0 right-0 z-20 px-6" data-testid="transform-progress-rail">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-2">
            {stages.map((s, i) => (
              <div key={i} className="flex flex-1 flex-col items-center gap-2">
                <div className={`h-1 w-full rounded-full transition-colors duration-500 ${i <= active ? "bg-[#4DB8FF]" : "bg-white/20"}`} />
                <span className={`hidden text-[10px] uppercase tracking-widest transition-colors duration-500 sm:block ${i === active ? "text-white" : "text-white/35"}`}>
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
